/**
 * v-gtm-events.js — GA4 tracking (skill-aligned)
 * - window.vGtm.push() for skill / new declarative events (data-v-gtm-*)
 * - window.GTMTrackerGA4 legacy API for existing data-ga4-* markup
 */
(function () {
  'use strict';

  const config = {
    debug: false,
    brand: 'Levoit',
    defaultCurrency: 'USD',
  };

  function log(...args) {
    if (config.debug) {
      // console.log('[GTM GA4]', ...args);
      console.log('[GTM GA4]', JSON.stringify(args));
    }
  }

  function normalizeEventParams(eventName, eventParameters) {
    const params = Object.assign({}, eventParameters);

    // Excel 规范用 link_url；声明式埋点常写 data-v-gtm-url / params.url（$url 占位符）
    if (eventName !== 'generate_lead') {
      if (params.url && !params.link_url) {
        params.link_url = params.url;
      }
      if (params.link_url) {
        delete params.url;
      }
    }

    return params;
  }

  function pushGA4Event(eventName, eventParameters = {}) {
    window.dataLayer = window.dataLayer || [];

    const params = normalizeEventParams(eventName, eventParameters);

    window.dataLayer.push({ event_parameters: null });
    window.dataLayer.push({
      event: 'ga4Event',
      event_name: eventName,
      event_parameters: params,
    });

    log('GA4 Event:', eventName, params);
  }

  function formatItems(items, module = '') {
    if (!Array.isArray(items)) {
      items = [items];
    }

    return items.map((item) => {
      const listId = item.item_list_id || module || '';
      const listName = item.item_list_name || item.item_list_id || module || '';
      return {
        item_id: item.item_id || item.id || '',
        item_name: item.item_name || item.name || '',
        item_brand: config.brand,
        item_category: item.item_category || item.category || '',
        item_variant: item.item_variant || item.variant || '',
        price: parseFloat(item.price) || 0,
        quantity: parseInt(item.quantity) || 1,
        discount: parseFloat(item.discount) || 0,
        index: item.index || 0,
        item_list_id: listId,
        item_list_name: listName,
      };
    });
  }

  const GA4 = {
    viewItem: function (itemData, module = 'Product Details Page') {
      const items = formatItems(itemData, module);
      const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

      pushGA4Event('view_item', {
        currency: config.defaultCurrency,
        value: value,
        module: module,
        items: items,
      });
    },

    addToCart: function (itemData, params = {}) {
      const module = params.module || 'Product Details Page';
      const items = formatItems(itemData, module);
      const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

      pushGA4Event('add_to_cart', {
        currency: config.defaultCurrency,
        value: value,
        module: module,
        button_name: params.button_name || 'Add to Cart',
        items: items,
      });
    },

    removeFromCart: function (itemData, params = {}) {
      const module = params.module || 'Cart Popup';
      const items = formatItems(itemData, module);
      const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

      pushGA4Event('remove_from_cart', {
        module: module,
        currency: config.defaultCurrency,
        value: value,
        button_name: params.button_name || 'Remove',
        items: items,
      });
    },

    decreaseQuantity: function (itemData, params = {}) {
      pushGA4Event('decrease_quantity', {
        product_id: itemData.item_id || itemData.product_id || '',
        product_name: itemData.item_name || itemData.product_name || '',
        quantity: params.quantity || 1,
      });
    },

    increaseQuantity: function (itemData, params = {}) {
      pushGA4Event('increase_quantity', {
        product_id: itemData.item_id || itemData.product_id || '',
        product_name: itemData.item_name || itemData.product_name || '',
        quantity: params.quantity || 1,
      });
    },

    addAddressInfo: function (params = {}) {
      pushGA4Event('add_address_info', params);
    },

    addShippingInfo: function (itemsData, params = {}) {
      const items = formatItems(itemsData, params.module);
      const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

      pushGA4Event('add_shipping_info', {
        currency: config.defaultCurrency,
        value: value,
        items: items,
      });
    },

    addPaymentInfo: function (itemsData, params = {}) {
      const items = formatItems(itemsData, params.module);
      const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

      pushGA4Event('add_payment_info', {
        currency: config.defaultCurrency,
        value: value,
        items: items,
      });
    },

    purchase: function (itemsData, params = {}) {
      const items = formatItems(itemsData, params.module);
      const value =
        params.value != null
          ? parseFloat(params.value)
          : Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

      pushGA4Event('purchase', {
        currency: params.currency || config.defaultCurrency,
        value: value,
        transaction_id: params.transaction_id || '',
        items: items,
      });
    },

    viewItemList: function (itemsData, listName = '', module = '') {
      const items = formatItems(itemsData, module || listName);
      const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

      pushGA4Event('view_item_list', {
        item_list_name: module || listName,
        module: module,
        currency: config.defaultCurrency,
        value: value,
        items: items,
      });
    },

    selectItem: function (itemData, params = {}) {
      const items = formatItems(itemData, params.module);

      pushGA4Event('select_item', {
        module: params.module || '',
        item_list_id: params.module || '',
        currency: config.defaultCurrency,
        button_name: params.button_name || '',
        items: items,
      });
    },


    viewCart: function (itemsData, params = {}) {
      const module = params.module || 'Cart Popup';
      const items = formatItems(itemsData, module);
      const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

      pushGA4Event('view_cart', {
        module: module,
        currency: config.defaultCurrency,
        value: value,
        items: items,
      });
    },

    addToWishlist: function (itemData, params = {}) {
      const module = params.module || 'Favorite Page';
      const items = formatItems(itemData, module);
      const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

      pushGA4Event('add_to_wishlist', {
        currency: config.defaultCurrency,
        value: value,
        items: items,
      });
    },

    beginCheckout: function (itemsData, params = {}) {
      const items = formatItems(itemsData, params.module);
      const value = Math.round(items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;

      pushGA4Event('begin_checkout', {
        currency: config.defaultCurrency,
        value: value,
        coupon: params.coupon || '',
        items: items,
      });
    },

    search: function (searchTermOrParams, elementLocation = '') {
      if (typeof searchTermOrParams === 'object' && searchTermOrParams !== null) {
        pushGA4Event('search', {
          element_location: searchTermOrParams.element_location || '',
          search_term: searchTermOrParams.search_term || '',
          search_action: searchTermOrParams.search_action || 'search_submit',
        });
        return;
      }

      pushGA4Event('search', {
        element_location: elementLocation,
        search_term: searchTermOrParams,
        search_action: 'search_submit',
      });
    },

    scroll: function (percentScrolled) {
      pushGA4Event('scroll', {
        percent_scrolled: percentScrolled,
      });
    },

    cookieInteraction: function (params = {}) {
      pushGA4Event('cookie_interaction', {
        interaction_type: params.interaction_type || '',
        consent_action: params.consent_action || '',
        banner_version: params.banner_version || '',
        button_name: params.button_name || '',
        banner_location: params.banner_location || '',
      });
    },

    viewBanner: function (bannerName, position) {
      pushGA4Event('view_banner', {
        banner_location: position,
        banner_name: bannerName,
      });
    },

    promoStripClick: function (params = {}) {
      pushGA4Event('promo_strip_click', {
        link_url: params.link_url || '',
        link_text: params.link_text || '',
        promo_position: params.promo_position || '',
        path_location: params.path_location || '',
        promo_name: params.promo_name || '',
        button_name: params.button_name || '',
      });
    },

    bannerClick: function (params = {}) {
      pushGA4Event('banner_click', {
        banner_location: params.banner_location || '',
        banner_type: params.banner_type || 'promotional',
        banner_name: params.banner_name || '',
        button_name: params.button_name || 'Banner',
      });
    },

    navigationBarClick: function (params = {}) {
      pushGA4Event('navigation_bar_click', {
        link_url: params.link_url || '',
        link_text: params.link_text || '',
        navigation_category: params.navigation_category || '',
        navigation_sub_category: params.navigation_sub_category || '',
        navigation_sub_sub_category: params.navigation_sub_sub_category || '',
      });
    },

    topFunction: function (buttonName) {
      pushGA4Event('top_function', {
        button_name: buttonName,
      });
    },

    selectOtherBrand: function (url, buttonName) {
      pushGA4Event('select_other_brand', {
        link_url: url,
        button_name: buttonName,
      });
    },

    expressCheckout: function (buttonName, module = 'Cart Popup') {
      pushGA4Event('express_checkout', {
        module: module,
        button_name: buttonName,
      });
    },

    floatBar: function (buttonName) {
      pushGA4Event('float_bar', {
        button_name: buttonName,
      });
    },

    generateLead: function (params = {}) {
      pushGA4Event('generate_lead', {
        lead_source: params.lead_source || '',
        lead_type: params.lead_type || '',
        contact_source: params.contact_source || '',
        url: params.url || window.location.href,
      });
    },

    bottomNavigation: function (navigationGroup, navigationName) {
      pushGA4Event('bottom_navigation', {
        navigation_group: navigationGroup,
        navigation_name: navigationName,
      });
    },

    followUs: function (buttonNameOrParams, legacyButtonName) {
      if (typeof buttonNameOrParams === 'object' && buttonNameOrParams !== null) {
        pushGA4Event('follow_us', normalizeFollowUsParams(buttonNameOrParams));
        return;
      }

      pushGA4Event('follow_us', normalizeFollowUsParams({
        button_name: buttonNameOrParams || legacyButtonName || '',
      }));
    },

    login: function (method) {
      pushGA4Event('login', {
        method: method,
      });
    },

    signUp: function () {
      pushGA4Event('sign_up', {});
    },

    selectContent: function (contentTypeOrParams, contentName) {
      if (typeof contentTypeOrParams === 'object' && contentTypeOrParams !== null) {
        pushGA4Event('select_content', {
          content_type: contentTypeOrParams.content_type || contentTypeOrParams.module || '',
          content_name: contentTypeOrParams.content_name || contentTypeOrParams.button_name || '',
          link_url: contentTypeOrParams.link_url || '',
        });
        return;
      }

      pushGA4Event('select_content', {
        content_type: contentTypeOrParams || '',
        content_name: contentName || '',
      });
    },

    checkoutButton: function (params = {}) {
      pushGA4Event('checkout_button', {
        module: params.module || '',
        button_name: params.button_name || '',
      });
    },

    selectProduct: function (params = {}) {
      pushGA4Event('select_product', {
        module: params.module || '',
        button_name: params.button_name || '',
        product_name: params.product_name || '',
      });
    },
    
    watchVideo: function (params = {}) {
      pushGA4Event('video_view', {
        module: params.module || '',
        button_name: params.button_name || '',
      });
    },

    shopNow: function (params = {}) {
      pushGA4Event('shop_now', {
        module: params.module || '',
        button_name: params.button_name || '',
      });
    },
  };

  const trackerApi = {
    init: function (options = {}) {
      Object.assign(config, options);
      log('GA4 Tracker initialized', config);
    },
    ...GA4,
    config: config,
    pushEvent: pushGA4Event,
  };

  window.GTMTrackerGA4 = trackerApi;
  window.vGtm = {
    push: pushGA4Event,
    pushRaw: function (obj) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(obj);
      log('vGtm:raw', obj);
    },
    updateConsent: function (state) {
      if (typeof gtag === 'function') {
        gtag('consent', 'update', state);
      }
    },
    get debug() {
      return config.debug || new URLSearchParams(location.search).has('vgtm_debug');
    },
    cart: {
      trackAdd: function () {},
      trackView: function () {},
      trackLineChange: function () {},
    },
  };

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function gtmAttr(el, key) {
    return (el.getAttribute('data-v-gtm-' + key) || '').trim();
  }

  function bindLegacyGa4Events(tracker) {
    let isCartButtonTriggered = false;
    let cartButtonTimeout = null;

    function getProductDataFromElement(element) {
      let variantTitle = element.getAttribute('data-variant-title') || '';
      if (variantTitle === 'Default Title') {
        variantTitle = '';
      }
      return {
        item_id: element.getAttribute('data-product-sku') || '',
        item_name: element.getAttribute('data-product-title') || '',
        item_category: element.getAttribute('data-product-type') || '',
        item_variant: variantTitle,
        price: parseFloat(element.getAttribute('data-product-price')) || 0,
        quantity: parseInt(element.getAttribute('data-quantity')) || 1,
        index: parseInt(element.getAttribute('data-index')) || 0,
        discount: parseFloat(element.getAttribute('data-discount')) || 0,
      };
    }

    document.addEventListener(
      'mousedown',
      function (e) {
        const target = e.target.closest('[data-cart-quantity-change]');
        if (target) {
          isCartButtonTriggered = true;
          if (cartButtonTimeout) {
            clearTimeout(cartButtonTimeout);
          }
          cartButtonTimeout = setTimeout(function () {
            isCartButtonTriggered = false;
          }, 200);
        }
      },
      true
    );

    document.addEventListener('click', function (e) {
      const path = e.composedPath ? e.composedPath() : [e.target];
      let target = null;

      for (let i = 0; i < path.length; i++) {
        if (path[i].hasAttribute && path[i].hasAttribute('data-ga4-track')) {
          target = path[i];
          break;
        }
      }

      if (!target) return;

      if (target.hasAttribute('data-v-gtm-event')) return;

      const eventType = target.getAttribute('data-ga4-event');
      const module = target.getAttribute('data-ga4-module') || '';

      switch (eventType) {
        case 'select_item': {
          let selectItem = target;
          if (!selectItem.hasAttribute('data-product-sku')) {
            selectItem = target.closest('[data-product-sku]');
          }
          if (selectItem) {
            const productData = getProductDataFromElement(selectItem);
            tracker.selectItem(productData, {
              module: module,
              button_name:
                target.getAttribute('data-button-name') ||
                selectItem.getAttribute('data-button-name') ||
                '',
            });
          }
          break;
        }

        case 'checkout_button':
          tracker.checkoutButton({
            module: module || 'Cart Page',
            button_name: target.getAttribute('data-button-name') || 'Checkout',
          });
          break;

        case 'add_to_wishlist': {
          const wishlistItem = target.closest('[data-product-sku]');
          if (wishlistItem) {
            const productData = getProductDataFromElement(wishlistItem);
            tracker.addToWishlist(productData, {
              module: module || 'Favorite Page',
            });
          }
          break;
        }

        case 'banner_click':
        case 'promo_strip_click':
          tracker[eventType === 'banner_click' ? 'bannerClick' : 'promoStripClick']({
            link_text: target.textContent.trim(),
            link_url: target.getAttribute('data-link-url') || '',
            promo_position: target.getAttribute('data-promo-position') || '',
            promo_name: target.getAttribute('data-promo-name') || '',
            banner_name: target.getAttribute('data-banner-name') || '',
            banner_location: target.getAttribute('data-banner-location') || '',
            banner_type: target.getAttribute('data-banner-type') || '',
            button_name: target.getAttribute('data-button-name') || '',
            path_location: target.getAttribute('data-path-location') || '',
          });
          break;

        case 'navigation_bar_click':
          tracker.navigationBarClick({
            link_url: target.href || '',
            link_text: target.textContent.trim(),
            navigation_category: target.getAttribute('data-nav-category') || '',
            navigation_sub_category: target.getAttribute('data-nav-sub') || '',
            navigation_sub_sub_category: target.getAttribute('data-nav-sub-sub') || '',
          });
          break;

        case 'top_function':
          tracker.topFunction(target.getAttribute('data-button-name') || target.textContent.trim());
          break;

        case 'float_bar':
          tracker.floatBar(target.getAttribute('data-button-name') || target.textContent.trim());
          break;

        case 'express_checkout':
          tracker.expressCheckout(target.getAttribute('data-button-name') || '', module || 'Cart Popup');
          break;

        case 'bottom_navigation':
          tracker.bottomNavigation(
            target.getAttribute('data-nav-group') || '',
            target.getAttribute('data-nav-name') || target.textContent.trim()
          );
          break;

        case 'follow_us':
          tracker.followUs({
            module: module || target.getAttribute('data-ga4-module') || '',
            button_name: target.getAttribute('data-button-name') || target.textContent.trim(),
            link_url: target.href || target.getAttribute('data-link-url') || '',
          });
          break;

        case 'select_other_brand':
          tracker.selectOtherBrand(
            target.href || '',
            target.getAttribute('data-button-name') || target.textContent.trim()
          );
          break;

        case 'select_content':
          tracker.selectContent({
            content_type: target.getAttribute('data-content-type') || module,
            content_name: target.getAttribute('data-content-name') || target.textContent.trim(),
            link_url: target.href || target.getAttribute('data-link-url') || '',
          });
          break;

        case 'select_product':
          tracker.selectProduct({
            module: module || target.getAttribute('data-ga4-module') || '',
            button_name: target.getAttribute('data-button-name') || target.textContent.trim() || '',
            product_name: target.getAttribute('data-product-name') || '',
          });
          break;

        case 'shop_now':
          tracker.shopNow({
            module: module || target.getAttribute('data-ga4-module') || '',
            button_name: target.getAttribute('data-button-name') || target.textContent.trim() || '',
          });
          break;
      }
    });

    document.addEventListener('submit', function (e) {
      const form = e.target;

      if (form.matches('[data-newsletter-form]')) {
        const moduleName = form.getAttribute('data-module-name') || 'Newsletter';
        tracker.generateLead({
          lead_source: moduleName,
          lead_type: 'subscription',
          contact_source: 'email',
          url: window.location.href,
        });
      }
    });

    document.addEventListener('click', function (e) {
      const target = e.target;
      const moreOptionsLink =
        target.id === 'more-payment-options-link' ||
        target.classList.contains('shopify-payment-button__more-options') ||
        target.closest('more-payment-options-link') ||
        target.closest('.shopify-payment-button__more-options') ||
        target.closest('#more-payment-options-link');

      if (moreOptionsLink) {
        const actualTarget =
          target.id === 'more-payment-options-link' || target.classList.contains('shopify-payment-button__more-options')
            ? target
            : target.closest('more-payment-options-link') ||
              target.closest('.shopify-payment-button__more-options') ||
              target.closest('#more-payment-options-link');

        if (actualTarget) {
          const isProductPage = actualTarget.closest('product-form') !== null;

          if (isProductPage) {
            const buttonName = actualTarget.textContent.trim() || 'More payment options';
            tracker.checkoutButton({
              module: 'Product Details Page',
              button_name: buttonName,
            });
          }
        }
      }
    });

    let lastSearchTerm = '';
    let lastSearchTime = 0;
    const SEARCH_DEBOUNCE_TIME = 300;

    function trackSearch(searchTerm, location) {
      const currentTime = Date.now();
      if (searchTerm === lastSearchTerm && currentTime - lastSearchTime < SEARCH_DEBOUNCE_TIME) {
        return;
      }
      lastSearchTerm = searchTerm;
      lastSearchTime = currentTime;
      tracker.search({
        search_term: searchTerm,
        element_location: location,
        search_action: 'search_submit',
      });
    }

    const searchInputs = document.querySelectorAll('[data-search-input]');
    searchInputs.forEach(function (input) {
      if (input.closest('[data-v-gtm-scope="header"]')) return;

      input.addEventListener('keypress', function (e) {
        if (e.key === 'Enter' && input.value.trim()) {
          trackSearch(input.value.trim(), input.getAttribute('data-element-location') || '');
        }
      });
    });

    const searchSubmitButtons = document.querySelectorAll('[data-search-submit]');
    searchSubmitButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        const form = button.closest('[data-search-form]');
        if (!form) return;
        const input = form.querySelector('[data-search-input]');
        if (!input || input.closest('[data-v-gtm-scope="header"]')) return;
        if (input.value.trim()) {
          trackSearch(input.value.trim(), input.getAttribute('data-element-location') || '');
        }
      });
    });

    const banners = document.querySelectorAll('[data-banner-impression]');
    if (banners.length > 0 && 'IntersectionObserver' in window) {
      const bannerObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && !entry.target.hasAttribute('data-banner-viewed')) {
              entry.target.setAttribute('data-banner-viewed', 'true');
              tracker.viewBanner(
                entry.target.getAttribute('data-banner-name') || '',
                entry.target.getAttribute('data-banner-location') || ''
              );
            }
          });
        },
        { threshold: 0.5 }
      );

      banners.forEach(function (banner) {
        bannerObserver.observe(banner);
      });
    }

    const cookieBanner = document.querySelector('[data-cookie-banner]');
    if (cookieBanner) {
      cookieBanner.addEventListener('click', function (e) {
        const button = e.target.closest('button');
        if (button) {
          tracker.cookieInteraction({
            interaction_type: button.getAttribute('data-interaction-type') || 'click',
            consent_action: button.getAttribute('data-consent-action') || '',
            banner_version: cookieBanner.getAttribute('data-banner-version') || '1.0',
            button_name: button.textContent.trim(),
            banner_location: cookieBanner.getAttribute('data-banner-location') || 'bottom_bar',
          });
        }
      });
    }

    let lastCheckoutButtonClick = 0;
    const checkoutClickDebounce = 300;

    document.addEventListener(
      'click',
      function (e) {
        const now = Date.now();
        if (now - lastCheckoutButtonClick < checkoutClickDebounce) {
          return;
        }

        const path = e.composedPath ? e.composedPath() : [];
        let buttonName = null;
        let moduleValue = 'Cart Page';
        let isProductPage = false;

        for (let i = 0; i < path.length; i++) {
          const element = path[i];
          if (!element.tagName) continue;

          const tagName = element.tagName.toLowerCase();

          if (!buttonName) {
            if (tagName === 'shop-pay-wallet-button') {
              buttonName = 'Shop Pay';
            } else if (tagName === 'shopify-paypal-button') {
              buttonName = 'PayPal';
            } else if (tagName === 'shopify-google-pay-button') {
              buttonName = 'Google Pay';
            } else if (element.getAttribute && element.getAttribute('slot')) {
              const slot = element.getAttribute('slot');
              if (slot && slot.startsWith('button-')) {
                buttonName = slot.replace('button-', '');
              }
            }
          }

          if (tagName === 'product-form' || tagName === 'product-form-without-atc') {
            isProductPage = true;
          }

          if (element.classList && element.classList.contains('dtc-product-form__dynamic-checkout')) {
            isProductPage = true;
            const vMod = element.getAttribute('data-v-gtm-module');
            if (vMod) moduleValue = vMod;
          }

          if (element.getAttribute && element.getAttribute('data-v-gtm-module')) {
            const vMod = element.getAttribute('data-v-gtm-module');
            if (vMod && (isProductPage || element.classList.contains('dtc-product-form__dynamic-checkout'))) {
              moduleValue = vMod;
            }
          }

          if (element.classList && element.classList.contains('additional-checkout-buttons')) {
            const ga4Module = element.getAttribute('data-ga4-module');
            if (ga4Module) {
              moduleValue = ga4Module;
            }
            break;
          }
        }

        if (isProductPage && moduleValue === 'Cart Page') {
          moduleValue = 'Product Details Page';
        }

        if (buttonName) {
          lastCheckoutButtonClick = now;
          tracker.expressCheckout(buttonName, moduleValue);
        }
      },
      true
    );

    observeCartDrawerViewCart(document.querySelector('cart-drawer'), 'data-view-cart-tracked', function () {
      fetch(window.Shopify.routes.root + 'cart.js')
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          const cartItems =
            cart.items && cart.items.length > 0
              ? cart.items.map(function (item, index) {
                  const discountAmount = item.line_level_discount_allocations
                    ? item.line_level_discount_allocations.reduce(function (sum, allocation) {
                        return sum + allocation.amount;
                      }, 0) / 100
                    : 0;

                  return {
                    item_id: item.sku,
                    item_name: item.product_title,
                    discount: discountAmount,
                    index: index + 1,
                    item_category: item.product_type,
                    item_variant: item.variant_title,
                    price: item.price / 100,
                    quantity: item.quantity,
                  };
                })
              : [];

          tracker.viewCart(cartItems, {
            module: 'Cart Popup',
          });
        })
        .catch(function (error) {
          console.error('Failed to fetch cart:', error);
        });
    });
  }

  function txt(el) {
    return (el.getAttribute('data-v-gtm-text') || el.textContent || '').trim().replace(/\s+/g, ' ');
  }

  function headerNavLinkText(link) {
    const titleEl = link.querySelector('.dtc-header__mega-card-title');
    return titleEl ? txt(titleEl) : txt(link);
  }

  function otherBrandButtonName(link) {
    return link.getAttribute('aria-label') || link.querySelector('img')?.alt || txt(link) || 'Brand';
  }

  const searchEventDedupe = { key: '', time: 0 };

  function getSearchInputLocation(input) {
    if (!input) return '';
    const moduleEl = input.closest('[data-v-gtm-module]');
    return (
      input.getAttribute('data-element-location') ||
      input.getAttribute('data-v-gtm-element-location') ||
      gtmAttr(input, 'element-location') ||
      gtmAttr(input, 'module') ||
      (moduleEl && moduleEl.getAttribute('data-v-gtm-module')) ||
      ''
    );
  }

  function emitSearchEvent(push, params) {
    const term = (params.search_term || '').trim();
    if (!term) return;

    const action = params.search_action || 'search_submit';
    const location = params.element_location || '';
    const dedupeKey = term + '|' + action + '|' + location;
    const now = Date.now();
    if (dedupeKey === searchEventDedupe.key && now - searchEventDedupe.time < 300) return;
    searchEventDedupe.key = dedupeKey;
    searchEventDedupe.time = now;

    push('search', {
      search_term: term,
      element_location: location,
      search_action: action,
    });
  }

  function bindHeaderSearchEvents(push) {
    function resolveHeaderSearchInput(el) {
      const input = el.matches?.('[data-search-input]')
        ? el
        : el.closest?.('[data-search-form]')?.querySelector('[data-search-input]');
      if (!input || !input.closest('[data-v-gtm-scope="header"]')) return null;
      return input;
    }

    document.addEventListener(
      'submit',
      function (e) {
        const form = e.target;
        if (!form.matches?.('[data-search-form]')) return;
        const input = resolveHeaderSearchInput(form.querySelector('[data-search-input]') || form);
        if (!input || !input.value.trim()) return;
        emitSearchEvent(push, {
          search_term: input.value.trim(),
          element_location: getSearchInputLocation(input),
          search_action: 'search_submit',
        });
      },
      true
    );

    document.addEventListener(
      'click',
      function (e) {
        const btn = e.target.closest('[data-search-submit]');
        if (!btn) return;
        const input = resolveHeaderSearchInput(btn);
        if (!input || !input.value.trim()) return;
        emitSearchEvent(push, {
          search_term: input.value.trim(),
          element_location: getSearchInputLocation(input),
          search_action: 'search_submit',
        });
      },
      true
    );

    document.addEventListener(
      'keydown',
      function (e) {
        if (e.key !== 'Enter') return;
        const input = resolveHeaderSearchInput(e.target);
        if (!input || !input.value.trim()) return;

        const predictiveSearch = input.closest('predictive-search');
        if (
          predictiveSearch &&
          predictiveSearch.querySelector('[aria-selected="true"] a, [aria-selected="true"] button')
        ) {
          return;
        }

        emitSearchEvent(push, {
          search_term: input.value.trim(),
          element_location: getSearchInputLocation(input),
          search_action: 'search_submit',
        });
      },
      true
    );

    document.addEventListener(
      'click',
      function (e) {
        const link = e.target.closest('[data-predictive-search] a');
        if (!link) return;
        const predictiveSearch = link.closest('predictive-search');
        if (!predictiveSearch || !predictiveSearch.closest('[data-v-gtm-scope="header"]')) return;
        const input = predictiveSearch.querySelector('[data-search-input]');
        if (!input) return;

        emitSearchEvent(push, {
          search_term: input.value.trim() || txt(link),
          element_location: getSearchInputLocation(input),
          search_action: 'suggested_term_click',
        });
      },
      true
    );
  }

  function bindDeclarativeSearchInputs(push) {
    if (document.documentElement.dataset.vGtmDeclarativeSearchBound) return;
    document.documentElement.dataset.vGtmDeclarativeSearchBound = '1';

    const debounceTimers = new WeakMap();

    document.addEventListener(
      'keydown',
      function (e) {
        if (e.key !== 'Enter') return;
        const input = e.target;
        if (!input.matches?.('[data-v-gtm-search]')) return;
        const term = input.value.trim();
        if (!term) return;
        emitSearchEvent(push, {
          search_term: term,
          element_location: getSearchInputLocation(input),
          search_action: 'search_submit',
        });
      },
      true
    );

    document.addEventListener(
      'submit',
      function (e) {
        const form = e.target;
        if (!form.querySelector?.('[data-v-gtm-search]')) return;
        const input = form.querySelector('[data-v-gtm-search]');
        if (!input || !input.value.trim()) return;
        emitSearchEvent(push, {
          search_term: input.value.trim(),
          element_location: getSearchInputLocation(input),
          search_action: 'search_submit',
        });
      },
      true
    );

    document.addEventListener(
      'input',
      function (e) {
        const input = e.target;
        if (!input.matches?.('[data-v-gtm-search]')) return;
        if (input.closest('form.dtc-search-hero__search, form[action*="/search"]')) return;

        clearTimeout(debounceTimers.get(input));
        debounceTimers.set(
          input,
          setTimeout(function () {
            const term = input.value.trim();
            if (!term) return;
            emitSearchEvent(push, {
              search_term: term,
              element_location: getSearchInputLocation(input),
              search_action: 'search_submit',
            });
          }, 800)
        );
      },
      true
    );

    document.addEventListener(
      'click',
      function (e) {
        const chip = e.target.closest('[data-v-gtm-search-chip], .dtc-search-hero__chip');
        if (!chip || !chip.closest('.dtc-search-hero')) return;
        emitSearchEvent(push, {
          search_term: txt(chip),
          element_location: getSearchInputLocation(chip) || gtmAttr(chip, 'element-location') || '',
          search_action: 'suggested_term_click',
        });
      },
      true
    );
  }

  function bindBlogSidebarNavigation(push) {
    if (document.documentElement.dataset.vGtmBlogSidebarNavBound) return;
    document.documentElement.dataset.vGtmBlogSidebarNavBound = '1';

    document.addEventListener(
      'change',
      function (e) {
        const input = e.target;
        if (!input.matches?.('input[type="checkbox"], input[type="radio"]')) return;
        const navRoot = input.closest('[data-v-gtm-nav]');
        if (!navRoot || !navRoot.closest('.dtc-filters-blog')) return;
        if (!input.checked) return;

        const labelText = input.closest('label')?.querySelector('.dtc-filters-blog__option-text');
        push('select_navigation', {
          module:
            navRoot.getAttribute('data-v-gtm-module') ||
            (input.closest('[data-v-gtm-module]') || {}).getAttribute?.('data-v-gtm-module') ||
            '',
          nav_name: labelText ? txt(labelText) : input.value || '',
          link_url: window.location.href || '',
        });
      },
      true
    );
  }

  function bindHeaderTopFunctionEvents(push) {
    document.addEventListener(
      'click',
      function (e) {
        const path = e.composedPath ? e.composedPath() : [e.target];

        for (let i = 0; i < path.length; i++) {
          const el = path[i];
          if (!el || !el.closest) continue;
          if (!el.closest('[data-v-gtm-scope="header"]')) continue;

          if (el.closest('.dtc-header__logo')) {
            push('top_function', { button_name: 'Logo' });
            return;
          }

          if (el.closest('.dtc-header__cart-link, #cart-icon-bubble')) {
            push('top_function', { button_name: 'Cart' });
            return;
          }

          if (el.closest('.dtc-mobile-nav__footer a[href*="cart"]')) {
            push('top_function', { button_name: 'Cart' });
            return;
          }

          if (el.closest('.dtc-header__account-link')) {
            push('top_function', { button_name: 'Account' });
            return;
          }

          if (el.closest('.dtc-mobile-nav__footer .dtc-mobile-nav__btn--primary')) {
            push('top_function', { button_name: 'Account' });
            return;
          }

          if (el.closest('.dtc-header__search-tablet button, .dtc-header__search-tablet [data-search-drawer-trigger]')) {
            push('top_function', { button_name: 'Search' });
            return;
          }
        }
      },
      true
    );
  }

  function bindCookieYesInteraction(push) {
    if (document.documentElement.dataset.vGtmCookieYesBound) return;
    document.documentElement.dataset.vGtmCookieYesBound = '1';

    document.addEventListener(
      'click',
      function (e) {
        const btn = e.target.closest(
          '.cky-btn, .cky-notice-btn-wrapper button, button[class*="cky-"], [class*="cookieyes"] button'
        );
        if (!btn) return;

        let consentAction = '';
        if (btn.classList.contains('cky-btn-accept')) consentAction = 'accept';
        else if (btn.classList.contains('cky-btn-reject')) consentAction = 'reject';
        else if (btn.classList.contains('cky-btn-customize')) consentAction = 'preferences';

        push('cookie_interaction', {
          interaction_type: consentAction ? 'consent' : 'click',
          consent_action: consentAction,
          banner_version: '1.0',
          button_name: txt(btn),
          banner_location: 'bottom_bar',
        });
      },
      true
    );
  }

  const EMAIL_SIGNUP_FORM_SELECTOR = [
    'form[data-v-gtm-form="email_signup"]',
    '.dtc-footer__subscribe-form',
    '.dtc-newsletter-split__form',
    '.dtc-blog-detail-article__newsletter-form',
    '.dtc-newsletter-cta__form',
  ].join(', ');

  function resolveEmailSignupModule(el) {
    if (!el) return 'Newsletter';
    const form = el.matches && el.matches('form') ? el : el.closest('form');
    const source = form || el;
    const direct = source.getAttribute('data-v-gtm-module');
    if (direct) return direct;
    const ancestor = source.closest('[data-v-gtm-module]');
    if (ancestor) return ancestor.getAttribute('data-v-gtm-module') || 'Newsletter';
    if (source.closest('.dtc-footer__subscribe-form')) return 'Bottom Navigation';
    const section = source.closest('[id^="shopify-section-"]');
    if (section) {
      const heading = section.querySelector(
        'h1, h2, h3, .dtc-newsletter-split__heading, .dtc-newsletter-cta__heading, .dtc-blog-detail-article__newsletter-title'
      );
      if (heading) return txt(heading);
    }
    return 'Newsletter';
  }

  function isEmailSignupForm(form) {
    return !!(form && form.matches && form.matches(EMAIL_SIGNUP_FORM_SELECTOR));
  }

  function pushEmailSignupOnce(push, sourceEl, storageKey) {
    const key = storageKey || 'vgtm_email_signup_default';
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
    push('email_signup', {
      module: resolveEmailSignupModule(sourceEl),
    });
    return true;
  }

  function bindEmailSignupForms(push) {
    if (document.documentElement.dataset.vGtmEmailSignupFormsBound) return;
    document.documentElement.dataset.vGtmEmailSignupFormsBound = '1';

    document.addEventListener(
      'submit',
      function (e) {
        const form =
          e.target && e.target.closest
            ? e.target.closest('form')
            : e.target instanceof HTMLFormElement
              ? e.target
              : null;
        if (!form) return;
        if (!isEmailSignupForm(form)) return;
        if (form.checkValidity && !form.checkValidity()) return;

        const emailInput = form.querySelector('input[name="contact[email]"]');
        const email = emailInput && emailInput.value ? String(emailInput.value).trim() : '';
        if (!email) return;

        const storageKey =
          'vgtm_email_signup_' + (form.id || 'form') + '_' + email.toLowerCase();
        pushEmailSignupOnce(push, form, storageKey);
      },
      true
    );
  }

  function bindEmailSignupSuccess(push) {
    document.querySelectorAll('[data-v-gtm-email-signup-success]').forEach(function (el) {
      if (el.dataset.vGtmEmailSignupReported) return;
      el.dataset.vGtmEmailSignupReported = '1';

      const form = el.closest('form');
      const emailInput = form && form.querySelector('input[name="contact[email]"]');
      const email = emailInput && emailInput.value ? String(emailInput.value).trim().toLowerCase() : '';
      const storageKey =
        'vgtm_email_signup_' + (form && form.id ? form.id : 'success-marker') + '_' + (email || 'success');
      pushEmailSignupOnce(push, form || el, storageKey);
    });

    document
      .querySelectorAll(
        [
          '.dtc-newsletter-split__feedback--success',
          '.dtc-newsletter-cta__feedback--success',
          '.dtc-blog-detail-article__newsletter-message--success',
        ].join(', ')
      )
      .forEach(function (el) {
        if (el.dataset.vGtmEmailSignupReported) return;
        el.dataset.vGtmEmailSignupReported = '1';

        const form = el.closest('form');
        const storageKey = 'vgtm_email_signup_success_' + (form && form.id ? form.id : el.className);
        pushEmailSignupOnce(push, form || el, storageKey);
      });

    const params = new URLSearchParams(window.location.search);
    if (!params.has('customer_posted') && !params.has('contact_posted')) return;

    document.querySelectorAll(EMAIL_SIGNUP_FORM_SELECTOR).forEach(function (form) {
      if (form.dataset.vGtmEmailSignupReported) return;
      form.dataset.vGtmEmailSignupReported = '1';
      pushEmailSignupOnce(push, form, 'vgtm_email_signup_url_' + (form.id || 'fallback'));
    });
  }

  function bindLevoitSiteEvents(push) {
    function handleDtcHeaderNav(link) {
      if (!link || !link.href || link.href === '#') return;

      const linkText = headerNavLinkText(link);
      let navCategory = '';
      let navSub = '';
      let navSubSub = '';

      const topNavItem = link.closest('.dtc-header__nav-item');
      if (topNavItem) {
        const topLink = topNavItem.querySelector(':scope > .dtc-header__nav-link');
        navCategory = topLink ? txt(topLink) : '';
        if (link === topLink) {
          navSub = navCategory;
        } else {
          navSub = linkText;
        }
      } else if (link.closest('.dtc-header__learn-dropdown, .dtc-header__learn-panel')) {
        navCategory = 'Learn';
        navSub = linkText;
      } else if (link.closest('.dtc-header__mega')) {
        const topNavItemMega = link.closest('.dtc-header__nav-item');
        const topLink = topNavItemMega && topNavItemMega.querySelector(':scope > .dtc-header__nav-link');
        navCategory = topLink ? txt(topLink) : '';
        navSub = linkText;
      }

      push('navigation_bar_click', {
        link_url: link.href,
        link_text: linkText,
        navigation_category: navCategory,
        navigation_sub_category: navSub,
        navigation_sub_sub_category: navSubSub,
      });
    }

    document.addEventListener(
      'click',
      function (e) {
        const path = e.composedPath ? e.composedPath() : [e.target];

        for (let i = 0; i < path.length; i++) {
          const el = path[i];
          if (!el || !el.closest) continue;

          const brandLink = el.closest(
            '.dtc-header-top-bar__brand-link, .dtc-header__mega-brand-logo, .dtc-mobile-nav__mega-brand-logo'
          );
          if (brandLink) {
            push('select_other_brand', {
              link_url: brandLink.href || '',
              button_name: otherBrandButtonName(brandLink),
            });
            return;
          }

          const topBarSupportLink = el.closest('.dtc-header-top-bar__support-link');
          if (topBarSupportLink) {
            push('support_click', {
              link_url: topBarSupportLink.href || '',
              module: 'Header Top Bar',
              button_name: txt(topBarSupportLink) || 'Support',
            });
            return;
          }

          const header = el.closest('[data-v-gtm-scope="header"]');
          if (header) {
            const supportLink = el.closest('a[href*="support"]');
            if (supportLink && header.contains(supportLink)) {
              push('support_click', {
                link_url: supportLink.href || '',
                module: 'Top Function',
                button_name: txt(supportLink) || 'Support',
              });
              return;
            }

            const navLink = el.closest(
              '.dtc-header__nav a[href], .dtc-header__mega a[href], .dtc-header__learn-card[href], .dtc-header__learn-dropdown a[href]'
            );
            if (navLink && !navLink.closest('.dtc-header__mega-brands')) {
              handleDtcHeaderNav(navLink);
              return;
            }
          }

          const footer = el.closest('[data-v-gtm-scope="footer"]');
          if (footer) {
            const socialLink = el.closest('.dtc-footer__social-link');
            if (socialLink) {
              if (socialLink.hasAttribute('data-v-gtm-event')) return;
              const moduleEl = socialLink.closest('[data-v-gtm-module]');
              push('follow_us', normalizeFollowUsParams({
                module: (moduleEl && moduleEl.getAttribute('data-v-gtm-module')) || 'Bottom Function',
                button_name: socialLink.getAttribute('aria-label') || txt(socialLink),
                link_url: socialLink.href || '',
              }, socialLink));
              return;
            }

            const footerNavLink = el.closest('.dtc-footer__nav-link, .dtc-footer__official-websites-link');
            if (footerNavLink) {
              const col = footerNavLink.closest('.dtc-footer__nav-col');
              const headingBtn = col && col.querySelector('.dtc-footer__nav-heading');
              push('bottom_navigation', {
                navigation_group: headingBtn ? txt(headingBtn) : '',
                navigation_name: txt(footerNavLink),
                link_url: footerNavLink.href || '',
              });
              return;
            }
          }

          const faqTrigger = el.closest('[data-v-gtm-faq] .dtc-faq__trigger, [data-v-gtm-faq] [data-faq-trigger]');
          if (faqTrigger) {
            const container = faqTrigger.closest('[data-v-gtm-faq]');
            const question = faqTrigger.querySelector('.dtc-faq__question');
            push('select_faq', {
              module: (container && container.getAttribute('data-v-gtm-module')) || '',
              button_name: question ? txt(question) : txt(faqTrigger),
            });
            return;
          }

          const cookieBanner = el.closest('[data-v-gtm-cookie-banner]');
          if (cookieBanner) {
            const button = el.closest('button');
            if (button) {
              push('cookie_interaction', {
                interaction_type: button.getAttribute('data-v-gtm-interaction-type') || 'click',
                consent_action: button.getAttribute('data-v-gtm-consent-action') || '',
                banner_version: cookieBanner.getAttribute('data-v-gtm-banner-version') || '1.0',
                button_name: txt(button),
                banner_location: cookieBanner.getAttribute('data-v-gtm-banner-location') || 'bottom_bar',
              });
            }
            return;
          }
        }
      },
      true
    );

    bindHeaderSearchEvents(push);
    bindCookieYesInteraction(push);
    bindEmailSignupForms(push);
    bindEmailSignupSuccess(push);
  }

  function collectGtmParams(target) {
    const params = {};
    const attrs = target.attributes;
    for (let i = 0; i < attrs.length; i++) {
      const name = attrs[i].name;
      if (name.indexOf('data-v-gtm-') === 0 && name !== 'data-v-gtm-event') {
        params[name.slice(11).replace(/-/g, '_')] = attrs[i].value;
      }
    }
    if (!params.module) {
      const moduleAncestor = target.closest('[data-v-gtm-module]');
      if (moduleAncestor) params.module = moduleAncestor.getAttribute('data-v-gtm-module');
    }
    if (!params.link_url && params.url) {
      params.link_url = params.url;
      delete params.url;
    }
    if (!params.link_url && target.tagName === 'A' && target.href) {
      params.link_url = target.href;
    }
    return params;
  }

  function normalizeSelectContentParams(params, el) {
    return {
      content_type: params.content_type || params.module || '',
      content_name: params.content_name || params.button_name || (el ? txt(el) : ''),
      link_url: params.link_url || (el && el.href) || '',
    };
  }

  function resolveDtcSectionModule(el) {
    const moduleEl = el.closest('[data-v-gtm-module]');
    if (moduleEl) return moduleEl.getAttribute('data-v-gtm-module') || '';

    const sectionRoot = el.closest('[id^="shopify-section-"]');
    if (!sectionRoot) return '';

    const heading = sectionRoot.querySelector(
      '.dtc-section-header__heading, .dtc-section-header__title, .dtc-multicolumn__title, .dtc-common-gradient-cta__heading, .dtc-app-ecosystem-hero__heading, .dtc-app-ecosystem-hero__mobile-heading, h1[class*="dtc-"], h2[class*="dtc-"]'
    );
    return heading ? txt(heading) : '';
  }

  function resolveBannerClickType(params, el, clickedEl) {
    const clickSource = clickedEl || el;
    if (
      clickSource &&
      clickSource.closest(
        '.dtc-plp-product-loop-banner__button, [class*="__button"]:not([class*="__button--secondary"])'
      )
    ) {
      return 'primary_cta';
    }
    if (params.click_type) {
      if (params.click_type === 'button') return 'primary_cta';
      return params.click_type;
    }
    if (!el) return 'image';
    if (
      el.matches(
        '.dtc-home-hero__product, .dtc-home-hero-slider__product, [data-v-gtm-banner-click-type="image"], img, picture'
      )
    ) {
      return 'image';
    }
    if (el.matches('[class*="__button--secondary"], [class*="--secondary"]')) return 'secondary_cta';
    if (el.matches('a, button, [role="button"]')) return 'primary_cta';
    return 'image';
  }

  function normalizeBannerClickParams(params, el, clickedEl) {
    const banner = el && el.closest('[data-v-gtm-banner]');
    return {
      banner_name:
        params.banner_name ||
        (banner ? banner.getAttribute('data-v-gtm-banner-name') : '') ||
        params.module ||
        '',
      banner_location:
        params.banner_location ||
        (banner ? banner.getAttribute('data-v-gtm-banner-location') : '') ||
        'hero_banner',
      banner_type:
        params.banner_type ||
        (banner ? banner.getAttribute('data-v-gtm-banner-type') : '') ||
        'promotional',
      click_type: resolveBannerClickType(params, el, clickedEl),
      button_name: params.button_name || (clickedEl ? txt(clickedEl) : el ? txt(el) : ''),
      link_url: params.link_url || (el && el.href) || '',
    };
  }

  function normalizeFollowUsParams(params, el) {
    const moduleEl = el && el.closest('[data-v-gtm-module]');
    return {
      module:
        params.module ||
        params.module_name ||
        (moduleEl && moduleEl.getAttribute('data-v-gtm-module')) ||
        '',
      button_name: params.button_name || (el ? el.getAttribute('aria-label') || txt(el) : ''),
      link_url: params.link_url || (el && el.href) || '',
    };
  }

  function normalizeSelectArticleParams(params, el) {
    const category = params.article_category || params.category_name || '';
    return {
      content_type: params.content_type || 'blog',
      module: params.module || '',
      article_category: category || 'none',
      article_name: params.article_name || params.content_name || (el ? txt(el) : ''),
      article_position: params.article_position || params.index || '',
      link_url: params.link_url || (el && el.href) || '',
    };
  }

  const DTC_SELECT_CONTENT_CTA_SELECTOR = [
    'a.dtc-multicolumn__footer-btn',
    'a.dtc-gradient-cta__button',
    'a.dtc-common-gradient-cta__button',
    'a.dtc-learn-topic-tabs__button',
    'a.dtc-vacuum-comparison__cta-btn',
    'a.dtc-affiliate-comparison__button',
    'a.dtc-promo-countdown__btn',
    'a.dtc-shop-cat-nav__cta',
    'a.dtc-collection-promo-slider__button',
    'a.dtc-scientific__cta',
    'a.dtc-newsletter-cta__btn',
  ].join(', ');

  function resolveFacetFilterValue(input) {
    const explicit = gtmAttr(input, 'filter-value');
    if (explicit) return explicit;

    const labelEl = input.labels && input.labels[0] ? input.labels[0] : null;
    if (labelEl) {
      const visible = labelEl.querySelector('[aria-hidden="true"]');
      if (visible) return txt(visible);
      return txt(labelEl);
    }

    return input.value || '';
  }

  function resolveSortOption(input) {
    return (
      gtmAttr(input, 'sort-option') ||
      input.getAttribute('title') ||
      (input.labels && input.labels[0] ? txt(input.labels[0]) : '') ||
      input.value ||
      ''
    );
  }

  function bindFacetSortTracking(push) {
    if (document.documentElement.dataset.vGtmFacetSortBound) return;
    document.documentElement.dataset.vGtmFacetSortBound = '1';

    document.addEventListener(
      'input',
      function (e) {
        const input = e.target;
        if (!input || !input.matches) return;
        if (!input.matches('[data-v-gtm-sort], [data-v-gtm-event="select_sort"]')) return;
        if (!input.closest('#FacetSortForm, #FacetFiltersFormMobile')) return;
        if (input.type === 'radio' && !input.checked) return;

        push('select_sort', {
          module:
            gtmAttr(input, 'module') ||
            (input.closest('[data-v-gtm-module]') || {}).getAttribute?.('data-v-gtm-module') ||
            'Collection',
          sort_option: resolveSortOption(input),
        });
      },
      true
    );
  }

  function bindFacetFilterTracking(push) {
    if (document.documentElement.dataset.vGtmFacetFilterBound) return;
    document.documentElement.dataset.vGtmFacetFilterBound = '1';

    document.addEventListener(
      'input',
      function (e) {
        const input = e.target;
        if (!input || !input.matches) return;
        if (!input.matches('[data-v-gtm-filter], [data-v-gtm-event="select_filter"]')) return;
        if (!input.closest('#FacetFiltersForm, #FacetFiltersFormMobile')) return;

        push('select_filter', {
          module:
            gtmAttr(input, 'module') ||
            (input.closest('[data-v-gtm-module]') || {}).getAttribute?.('data-v-gtm-module') ||
            'Collection',
          filter_category: gtmAttr(input, 'filter-name') || gtmAttr(input, 'filter_category') || '',
          filter_value: resolveFacetFilterValue(input),
          filter_action: input.checked ? 'select' : 'deselect',
        });
      },
      true
    );
  }

  function bindDtcSelectContentEvents(push) {
    document.addEventListener(
      'click',
      function (e) {
        const link = e.target.closest(DTC_SELECT_CONTENT_CTA_SELECTOR);
        if (!link || !link.closest('[class*="dtc-"]')) return;
        if (link.hasAttribute('data-v-gtm-event')) return;
        if (link.closest('[data-v-gtm-event]') && link.closest('[data-v-gtm-event]') !== link) return;
        if (link.closest('[data-v-gtm-item-list], [data-v-gtm-event="select_item"], .dtc-product-card, [data-v-gtm-app-download]')) {
          return;
        }

        const href = link.getAttribute('href');
        if (href === null) return;

        push('select_content', {
          content_type: resolveDtcSectionModule(link),
          content_name: txt(link),
          link_url: href === '#' ? '#' : link.href || href,
        });
      },
      true
    );
  }

  function buildItemFromTarget(target) {
    return {
      item_id: gtmAttr(target, 'item-id') || gtmAttr(target, 'item_id') || '',
      item_name: gtmAttr(target, 'item-name') || gtmAttr(target, 'item_name') || '',
      item_category: gtmAttr(target, 'item-category') || gtmAttr(target, 'item_category') || '',
      item_variant: gtmAttr(target, 'item-variant') || gtmAttr(target, 'item_variant') || '',
      price: parseFloat(gtmAttr(target, 'price')) || 0,
      quantity: parseInt(gtmAttr(target, 'quantity'), 10) || 1,
      discount: parseFloat(gtmAttr(target, 'discount')) || 0,
      index: parseInt(gtmAttr(target, 'index'), 10) || 0,
      item_list_id: gtmAttr(target, 'item-list-id') || gtmAttr(target, 'item_list_id') || '',
      item_list_name: gtmAttr(target, 'item-list-name') || gtmAttr(target, 'item_list_name') || '',
    };
  }

  function getViewItemListSeenKeys(listEl) {
    return new Set((listEl.getAttribute('data-v-gtm-seen-items') || '').split('|').filter(Boolean));
  }

  function markViewItemListSeen(listEl, keys) {
    const seen = getViewItemListSeenKeys(listEl);
    keys.forEach(function (key) {
      if (key) seen.add(key);
    });
    listEl.setAttribute('data-v-gtm-seen-items', Array.from(seen).join('|'));
  }

  function pickViewItemListSource(root) {
    if (!root) return null;
    return (
      root.querySelector('[data-v-gtm-event="select_item"]') ||
      (root.hasAttribute('data-v-gtm-item-id') ? root : null) ||
      root.querySelector('[data-v-gtm-item-id]')
    );
  }

  function getSlideImpressionKey(slide, item) {
    if (slide && slide.id) return 'slide:' + slide.id;
    const id = (item && item.item_id) || '';
    const name = (item && item.item_name) || '';
    const base = id ? 'id:' + id : name ? 'name:' + name : 'item';
    const idx =
      (item && item.index) ||
      (slide && slide.parentElement
        ? Array.prototype.indexOf.call(slide.parentElement.children, slide) + 1
        : '');
    return 'slide:' + base + (idx ? '@' + idx : '');
  }

  function resolveSwiperFromListEl(listEl) {
    if (!listEl) return null;
    const host = listEl.closest('swiper-slider');
    const root = listEl.classList.contains('swiper-wrapper')
      ? listEl.parentElement
      : listEl.closest('.swiper');
    return (host && host.swiper) || (root && root.swiper) || null;
  }

  function resolveSwiperSlidesPerView(swiper) {
    if (!swiper || !swiper.params) return 1;
    const raw = swiper.params.slidesPerView;
    if (raw === 'auto') {
      return typeof swiper.slidesPerViewDynamic === 'function'
        ? Math.max(1, swiper.slidesPerViewDynamic() || 1)
        : 1;
    }
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  function resolveCarouselNavDirection(btn) {
    if (!btn) return 1;
    if (btn.matches('[swiper-button-prev], .dtc-multicolumn__nav-btn--prev')) return -1;
    if (btn.matches('[swiper-button-next], .dtc-multicolumn__nav-btn--next')) return 1;
    const hint = (
      btn.getAttribute('data-v-gtm-button_name') ||
      btn.getAttribute('aria-label') ||
      txt(btn) ||
      ''
    ).toLowerCase();
    if (hint.indexOf('prev') !== -1 || hint.indexOf('left') !== -1 || hint.indexOf('arrow_left') !== -1) {
      return -1;
    }
    return 1;
  }

  function collectItemsFromSlides(slides) {
    const items = [];
    const localSeen = new Set();

    slides.forEach(function (slide, idx) {
      if (!slide || slide.classList.contains('swiper-slide-duplicate')) return;
      const source = pickViewItemListSource(slide);
      if (!source) return;

      const item = buildItemFromTarget(source);
      const slideIndex = slide.parentElement
        ? Array.prototype.indexOf.call(slide.parentElement.children, slide)
        : -1;
      if (!item.index) item.index = slideIndex > -1 ? slideIndex + 1 : idx + 1;

      const key = getSlideImpressionKey(slide, item);
      if (!key || localSeen.has(key)) return;
      localSeen.add(key);
      item.__impressionKey = key;
      items.push(item);
    });

    return items;
  }

  function getVisibleWindowSlides(swiper) {
    if (!swiper || !swiper.slides) return [];
    const slides = Array.from(swiper.slides);
    const perView = resolveSwiperSlidesPerView(swiper);
    const count = Math.max(1, Math.ceil(perView));
    const start = Math.max(0, swiper.activeIndex || 0);
    const visible = [];
    const seen = new Set();

    function add(slide) {
      if (!slide || slide.classList.contains('swiper-slide-duplicate') || seen.has(slide)) return;
      seen.add(slide);
      visible.push(slide);
    }

    for (let i = 0; i < count; i++) add(slides[start + i]);
    if (perView % 1 !== 0) add(slides[start + count]);

    if (swiper.isEnd || swiper.progress >= 0.99) {
      for (let i = slides.length - 1; i >= 0; i--) {
        if (pickViewItemListSource(slides[i])) {
          add(slides[i]);
          break;
        }
      }
    }

    return visible;
  }

  function getNewlyEnteringSlides(swiper, direction) {
    if (!swiper || !swiper.slides) return [];
    const slides = Array.from(swiper.slides);
    const perView = resolveSwiperSlidesPerView(swiper);
    const count = Math.max(1, Math.ceil(perView));
    const group = parseInt(swiper.params.slidesPerGroup, 10) || 1;
    const maxStart = Math.max(0, slides.length - count);
    const currentStart = Math.max(0, swiper.activeIndex || 0);
    let nextStart = currentStart + direction * group;
    nextStart = Math.max(0, Math.min(nextStart, maxStart));

    const currentVisible = new Set();
    for (let i = 0; i < count + (perView % 1 !== 0 ? 1 : 0); i++) {
      if (slides[currentStart + i]) currentVisible.add(slides[currentStart + i]);
    }

    const entering = [];
    const seen = new Set();
    function addEntering(slide) {
      if (!slide || slide.classList.contains('swiper-slide-duplicate')) return;
      if (currentVisible.has(slide) || seen.has(slide)) return;
      seen.add(slide);
      entering.push(slide);
    }

    for (let i = 0; i < count; i++) addEntering(slides[nextStart + i]);
    if (perView % 1 !== 0) addEntering(slides[nextStart + count]);

    if (direction > 0 && nextStart >= maxStart) {
      for (let i = slides.length - 1; i >= 0; i--) {
        if (pickViewItemListSource(slides[i])) {
          addEntering(slides[i]);
          break;
        }
      }
    }

    if (!entering.length) {
      for (let i = 0; i < count; i++) addEntering(slides[nextStart + i]);
    }

    return entering;
  }

  function pushViewItemListItems(listEl, push, currency, items) {
    if (!listEl || !items.length) return false;

    const panel = listEl.closest('[data-collection-panel]');
    if (panel && panel.hasAttribute('hidden')) return false;

    const seen = getViewItemListSeenKeys(listEl);
    const newItems = items.filter(function (item) {
      const key = item.__impressionKey || getSlideImpressionKey(null, item);
      return key && !seen.has(key);
    });
    if (!newItems.length) return false;

    markViewItemListSeen(
      listEl,
      newItems.map(function (item) {
        return item.__impressionKey || getSlideImpressionKey(null, item);
      })
    );

    const payload = newItems.map(function (item) {
      const copy = Object.assign({}, item);
      delete copy.__impressionKey;
      return copy;
    });

    const listName = listEl.getAttribute('data-v-gtm-item-list-name') || '';
    push('view_item_list', {
      item_list_name: listName,
      item_list_id: listEl.getAttribute('data-v-gtm-item-list-id') || listName,
      module: listEl.getAttribute('data-v-gtm-module') || '',
      currency: currency,
      items: payload,
    });
    return true;
  }

  function pushViewItemListFromSwiper(swiper, listEl, push, currency) {
    if (!swiper || !listEl) return false;
    return pushViewItemListItems(listEl, push, currency, collectItemsFromSlides(getVisibleWindowSlides(swiper)));
  }

  function pushViewItemListInitial(listEl, push, currency) {
    if (!listEl) return false;

    const swiper = resolveSwiperFromListEl(listEl);
    if (swiper && swiper.enabled !== false && swiper.el) {
      return pushViewItemListFromSwiper(swiper, listEl, push, currency);
    }

    // Non-swiper / not ready: only cards that currently intersect the viewport
    const viewport = {
      left: 0,
      top: 0,
      right: window.innerWidth || document.documentElement.clientWidth,
      bottom: window.innerHeight || document.documentElement.clientHeight,
    };
    const cards = Array.from(
      listEl.querySelectorAll('[data-v-gtm-item-id], [data-v-gtm-event="select_item"]')
    ).filter(function (card) {
      if (card.closest('[hidden]')) return false;
      const rect = card.getBoundingClientRect();
      return (
        rect.width > 0 &&
        rect.height > 0 &&
        rect.right > viewport.left &&
        rect.left < viewport.right &&
        rect.bottom > viewport.top &&
        rect.top < viewport.bottom
      );
    });

    const items = [];
    const localSeen = new Set();
    cards.forEach(function (card, idx) {
      const slide = card.closest('.swiper-slide');
      const item = buildItemFromTarget(card);
      if (!item.index) item.index = idx + 1;
      const key = getSlideImpressionKey(slide, item);
      if (!key || localSeen.has(key)) return;
      localSeen.add(key);
      item.__impressionKey = key;
      items.push(item);
    });

    return pushViewItemListItems(listEl, push, currency, items);
  }

  function flushViewItemListOnCarouselClick(navBtn, push, currency) {
    if (!navBtn) return false;

    const scope =
      navBtn.closest('swiper-slider') ||
      navBtn.closest('dtc-multicolumn') ||
      navBtn.closest('[id^="shopify-section-"]');
    if (!scope) return false;

    const listEl = scope.querySelector('[data-v-gtm-item-list]');
    if (!listEl) return false;

    const swiper = resolveSwiperFromListEl(listEl);
    if (!swiper || swiper.enabled === false) {
      return pushViewItemListInitial(listEl, push, currency);
    }

    const direction = resolveCarouselNavDirection(navBtn);
    const entering = getNewlyEnteringSlides(swiper, direction);
    const pushed = pushViewItemListItems(listEl, push, currency, collectItemsFromSlides(entering));

    setTimeout(function () {
      pushViewItemListFromSwiper(swiper, listEl, push, currency);
    }, 350);

    return pushed;
  }

  function bindViewItemListTracking(push, currency) {
    if (!('IntersectionObserver' in window)) return;

    const listObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          pushViewItemListInitial(entry.target, push, currency);
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -5% 0px' }
    );

    function checkVisibleItemLists() {
      document.querySelectorAll('[data-v-gtm-item-list]').forEach(function (el) {
        const rect = el.getBoundingClientRect();
        if (!rect.height) return;
        const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
        if (visibleHeight <= 0 || visibleHeight / rect.height < 0.2) return;
        pushViewItemListInitial(el, push, currency);
      });
    }

    function bindSwiperList(listEl) {
      const swiperRoot =
        listEl.classList.contains('swiper-wrapper') ? listEl.parentElement : listEl.closest('.swiper');
      if (!swiperRoot || swiperRoot.dataset.vGtmSwiperListBound) return;

      const flush = function () {
        const swiper = resolveSwiperFromListEl(listEl);
        if (swiper) pushViewItemListFromSwiper(swiper, listEl, push, currency);
      };

      const tryBind = function () {
        const swiper = resolveSwiperFromListEl(listEl);
        if (!swiper) return false;
        if (swiper.__vGtmViewItemListBound) return true;
        swiper.__vGtmViewItemListBound = true;
        swiper.on('slideChange', flush);
        swiper.on('slideChangeTransitionEnd', flush);
        swiper.on('reachEnd', flush);
        return true;
      };

      if (tryBind()) {
        swiperRoot.dataset.vGtmSwiperListBound = '1';
      } else {
        let attempts = 0;
        const timer = setInterval(function () {
          attempts += 1;
          if (tryBind() || attempts > 40) {
            if (tryBind()) swiperRoot.dataset.vGtmSwiperListBound = '1';
            clearInterval(timer);
          }
        }, 200);
      }
    }

    document.querySelectorAll('[data-v-gtm-item-list]').forEach(function (el) {
      listObserver.observe(el);
      bindSwiperList(el);
    });

    if (!document.documentElement.dataset.vGtmSwiperNavFlushBound) {
      document.documentElement.dataset.vGtmSwiperNavFlushBound = '1';
      document.addEventListener(
        'click',
        function (e) {
          const navBtn = e.target.closest(
            '.dtc-multicolumn__nav-btn, [swiper-button-prev], [swiper-button-next]'
          );
          if (!navBtn) return;
          flushViewItemListOnCarouselClick(navBtn, push, currency);
        },
        true
      );
    }

    window.addEventListener('v-gtm:check-item-lists', checkVisibleItemLists);
    requestAnimationFrame(checkVisibleItemLists);
    setTimeout(checkVisibleItemLists, 600);
  }

  function bindDtcEvents(push) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';

    bindDtcSelectContentEvents(push);

    document.addEventListener(
      'click',
      function (e) {
        const el = e.target;

        const tabBtn = el.closest(
          '[data-v-gtm-tabs] [role="tab"], .dtc-air-solution-tabs__nav-btn, .dtc-product-tabs__tab, .dtc-learn-topic-tabs__tab, .dtc-better-air-product-grid__tab, [data-v-gtm-tab]'
        );
        if (tabBtn) {
          const scope = tabBtn.closest('[data-v-gtm-tabs], [data-v-gtm-module], [class*="dtc-"]');
          push('tab_click', {
            module: (scope && scope.getAttribute('data-v-gtm-module')) || gtmAttr(scope, 'module') || '',
            button_name: txt(tabBtn),
          });
          return;
        }

        const techAccordionItem = el.closest('.dtc-tech-feature-accordion__item[data-feature-item]');
        if (techAccordionItem && techAccordionItem.closest('dtc-tech-feature-accordion')) {
          if (!techAccordionItem.classList.contains('is-open') && !techAccordionItem.hasAttribute('data-v-gtm-event')) {
            const titleEl = techAccordionItem.querySelector('.dtc-tech-feature-accordion__item-title');
            const scope = techAccordionItem.closest('[data-v-gtm-module]');
            push('select_content', {
              content_type: (scope && scope.getAttribute('data-v-gtm-module')) || resolveDtcSectionModule(techAccordionItem),
              content_name: titleEl ? txt(titleEl) : txt(techAccordionItem),
              link_url: '',
            });
          }
          return;
        }

        const appLink = el.closest(
          '[data-v-gtm-app-download], .dtc-vesync-app-download-bar__store-badge, .dtc-vesync-app-download__store-badge, a[href*="apps.apple.com"], a[href*="play.google.com"]'
        );
        if (appLink && appLink.closest('[class*="dtc-"]')) {
          const scope = appLink.closest('[data-v-gtm-module]');
          push('app_download', {
            module: (scope && scope.getAttribute('data-v-gtm-module')) || 'VeSync App',
            button_name: appLink.getAttribute('aria-label') || txt(appLink),
            link_url: appLink.href || '',
          });
          return;
        }

        const navLink = el.closest('.dtc-shop-category-navigation a[href], [data-v-gtm-nav] a[href]');
        if (navLink && !navLink.hasAttribute('data-v-gtm-event')) {
          const scope = navLink.closest('[data-v-gtm-module]');
          push('select_navigation', {
            module: (scope && scope.getAttribute('data-v-gtm-module')) || '',
            nav_name: txt(navLink),
            link_url: navLink.href || '',
          });
          return;
        }

        const pageLink = el.closest('.dtc-pagination__link[href]');
        if (pageLink) {
          push('switch_page', {
            module: (pageLink.closest('[data-v-gtm-module]') || {}).getAttribute?.('data-v-gtm-module') || 'Pagination',
            button_name: txt(pageLink),
            link_url: pageLink.href || '',
          });
          return;
        }

        const multicolumnNav = el.closest('.dtc-multicolumn__nav-btn');
        if (multicolumnNav && !multicolumnNav.hasAttribute('data-v-gtm-event')) {
          const scope = multicolumnNav.closest('[data-v-gtm-module]');
          const isPrev = multicolumnNav.matches('.dtc-multicolumn__nav-btn--prev, [swiper-button-prev]');
          push('carousel_navigation', {
            module: (scope && scope.getAttribute('data-v-gtm-module')) || '',
            button_name: isPrev ? 'arrow_left' : 'arrow_right',
          });
          flushViewItemListOnCarouselClick(multicolumnNav, push, currency);
          return;
        }

        const fileLink = el.closest('[data-v-gtm-file-download], a[data-v-gtm-event="file_download"]');
        if (fileLink) {
          push('file_download', {
            module: gtmAttr(fileLink, 'module') || (fileLink.closest('[data-v-gtm-module]') || {}).getAttribute?.('data-v-gtm-module') || '',
            file_name: gtmAttr(fileLink, 'file-name') || gtmAttr(fileLink, 'file_name') || txt(fileLink),
            link_url: fileLink.href || '',
          });
          return;
        }

        const social = el.closest(
          '.dtc-social-links__link, .dtc-social-links__button, .dtc-blog-detail-article__social-link, [data-v-gtm-event="follow_us"]'
        );
        if (social && !social.closest('[data-v-gtm-scope="footer"]') && !social.hasAttribute('data-v-gtm-event')) {
          push('follow_us', normalizeFollowUsParams({
            module: gtmAttr(social, 'module') || (social.closest('[data-v-gtm-module]') || {}).getAttribute?.('data-v-gtm-module') || '',
            button_name: social.getAttribute('aria-label') || txt(social),
            link_url: social.href || '',
          }, social));
          return;
        }

        const langControl = el.closest('[data-v-gtm-event="select_language"]');
        if (langControl) {
          push('select_language', collectGtmParams(langControl));
          return;
        }
      },
      true
    );

    bindDeclarativeSearchInputs(push);
    bindBlogSidebarNavigation(push);

    document.querySelectorAll('[data-v-gtm-form-interaction] form, form[data-v-gtm-form-interaction]').forEach(function (form) {
      if (form.dataset.vGtmFormBound) return;
      form.dataset.vGtmFormBound = '1';
      form.addEventListener(
        'focusin',
        function (e) {
          if (!e.target.matches('input, textarea, select')) return;
          push('form_interaction', {
            module: form.getAttribute('data-v-gtm-module') || '',
            form_name: form.getAttribute('data-v-gtm-form-name') || form.getAttribute('name') || '',
            field_name: e.target.name || e.target.id || '',
            interaction_type: 'focus',
          });
        },
        true
      );
    });

    bindFacetFilterTracking(push);
    bindFacetSortTracking(push);

    if ('IntersectionObserver' in window) {
      const bannerObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting || entry.target.hasAttribute('data-v-gtm-banner-viewed')) return;
            entry.target.setAttribute('data-v-gtm-banner-viewed', '1');
            push('view_banner', {
              banner_name: entry.target.getAttribute('data-v-gtm-banner-name') || '',
              banner_location: entry.target.getAttribute('data-v-gtm-banner-location') || 'hero_banner',
            });
          });
        },
        { threshold: 0.5 }
      );
      document.querySelectorAll('[data-v-gtm-banner]').forEach(function (el) {
        bannerObserver.observe(el);
      });
    }

    bindViewItemListTracking(push, currency);
  }

  function resolveDeclarativeGtmClickTarget(e) {
    const path = e.composedPath ? e.composedPath() : [e.target];

    for (let i = 0; i < path.length; i++) {
      if (path[i].hasAttribute && path[i].hasAttribute('data-v-gtm-event')) {
        return path[i];
      }
    }

    if (
      e.target.closest(
        '.dtc-product-card__favorite, add-to-favorites, [data-dtc-add-to-cart], .dtc-product-card__atc-btn, [data-v-gtm-event="add_to_wishlist"], [data-v-gtm-event="add_to_cart"]'
      )
    ) {
      return null;
    }

    const card = e.target.closest('.dtc-product-card');
    if (!card) return null;

    return card.querySelector('[data-v-gtm-event="select_item"]');
  }

  function bindSkillDeclarativeEvents(push) {
    // Declarative DTC events: select_collection, select_category, select_article, select_content, select_item, add_to_wishlist
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    let lastCheckoutButtonKey = '';
    let lastCheckoutButtonTime = 0;
    let lastFormSubmissionKey = '';
    let lastFormSubmissionTime = 0;

    document.addEventListener(
      'click',
      function (e) {
        const target = resolveDeclarativeGtmClickTarget(e);

        if (!target) return;

        const gtmEvent = target.getAttribute('data-v-gtm-event');
        if (!gtmEvent) return;

        // add-to-favorites reports add_to_wishlist only after a successful add (theme.js)
        if (gtmEvent === 'add_to_wishlist' && target.closest('add-to-favorites')) return;

        let params = collectGtmParams(target);

        if (gtmEvent === 'select_category') {
          push('select_category', {
            module: params.module || '',
            link_url: params.link_url || target.href || '',
            button_name: params.button_name || params.category_name || txt(target),
          });
          return;
        }

        if (gtmEvent === 'select_collection') {
          push('select_collection', {
            module: params.module || '',
            link_url: params.link_url || target.href || '',
            button_name: params.button_name || params.collection_name || txt(target),
          });
          return;
        }

        if (gtmEvent === 'select_item' || gtmEvent === 'add_to_wishlist') {
          const item = buildItemFromTarget(target);
          const listId = params.item_list_id || params.module || item.item_list_id || '';
          const listName = params.item_list_name || item.item_list_name || listId;
          item.item_list_id = item.item_list_id || listId;
          item.item_list_name = item.item_list_name || listName;

          if (gtmEvent === 'select_item') {
            const clickedLink = e.target.closest('a[href]');
            const buttonName =
              params.button_name ||
              (clickedLink && clickedLink.classList.contains('dtc-product-card__title-link')
                ? 'Title'
                : clickedLink && clickedLink.classList.contains('dtc-product-card__media-link')
                  ? 'Image'
                  : 'Card');
            push('select_item', {
              module: params.module || '',
              item_list_id: listId,
              currency: currency,
              button_name: buttonName,
              items: [item],
            });
            return;
          }

          push('add_to_wishlist', Object.assign(params, {
            currency,
            button_name: params.button_name || 'Card',
            item_list_id: listId,
            items: [item],
            value:
              params.value != null
                ? parseFloat(params.value)
                : Math.round(item.price * item.quantity * 100) / 100,
          }));
          return;
        }

        if (gtmEvent === 'select_content') {
          push('select_content', normalizeSelectContentParams(params, target));
          return;
        }

        if (gtmEvent === 'select_navigation') {
          push('select_navigation', {
            module: params.module || '',
            nav_name: params.navigation_name || params.nav_name || params.button_name || '',
            link_url: params.link_url || target.href || '',
          });
          return;
        }

        if (gtmEvent === 'banner_click') {
          push('banner_click', normalizeBannerClickParams(params, target, e.target));
          return;
        }

        if (gtmEvent === 'carousel_navigation') {
          push('carousel_navigation', {
            module: params.module || '',
            button_name: params.button_name || txt(target) || '',
          });
          flushViewItemListOnCarouselClick(target, push, currency);
          return;
        }

        if (gtmEvent === 'select_article') {
          push('select_article', normalizeSelectArticleParams(params, target));
          return;
        }

        if (gtmEvent === 'follow_us') {
          push('follow_us', normalizeFollowUsParams(params, target));
          return;
        }

        if (gtmEvent === 'checkout_button') {
          const checkoutKey = (params.module || 'Cart Popup') + '|' + (params.button_name || txt(target));
          const checkoutNow = Date.now();
          if (
            checkoutKey === lastCheckoutButtonKey &&
            checkoutNow - lastCheckoutButtonTime < 500
          ) {
            return;
          }
          lastCheckoutButtonKey = checkoutKey;
          lastCheckoutButtonTime = checkoutNow;
          push('checkout_button', {
            module: params.module || 'Cart Popup',
            button_name: params.button_name || txt(target),
          });
          return;
        }

        if (gtmEvent === 'form_submission') {
          const formKey = (params.module || '') + '|' + (params.button_name || txt(target));
          const formNow = Date.now();
          if (formKey === lastFormSubmissionKey && formNow - lastFormSubmissionTime < 500) {
            return;
          }
          lastFormSubmissionKey = formKey;
          lastFormSubmissionTime = formNow;
          push('form_submission', {
            module: params.module || '',
            button_name: params.button_name || txt(target),
          });
          return;
        }

        push(gtmEvent, params);
      },
      true
    );

    document.addEventListener(
      'toggle',
      function (e) {
        const details = e.target;
        if (!details || details.tagName !== 'DETAILS') return;
        if (!details.open) return;

        const container = details.closest('[data-v-gtm-faq]');
        if (container) {
          const summary = details.querySelector(':scope > summary');
          const buttonName = summary
            ? (summary.getAttribute('data-v-gtm-text') || summary.textContent || '').trim().replace(/\s+/g, ' ')
            : '';

          push('select_faq', {
            module: container.getAttribute('data-v-gtm-module') || '',
            button_name: buttonName,
          });
          return;
        }

        const accordionItem = details.closest('.dtc-app-feature-accordion__item');
        if (accordionItem && details.closest('dtc-app-feature-accordion')) {
          const titleEl = details.querySelector('.dtc-app-feature-accordion__title');
          const scope = details.closest('[data-v-gtm-module]');
          push('select_content', {
            content_type: (scope && scope.getAttribute('data-v-gtm-module')) || resolveDtcSectionModule(details),
            content_name: titleEl ? txt(titleEl) : txt(details),
            link_url: '',
          });
        }
      },
      true
    );
  }

  function shopifyLineToItem(line, index) {
    if (!line) return null;
    const variantTitle = line.variant_title === 'Default Title' ? '' : line.variant_title || '';
    const discountAmount = line.line_level_discount_allocations
      ? line.line_level_discount_allocations.reduce(function (sum, allocation) {
          return sum + allocation.amount;
        }, 0) / 100
      : 0;

    return {
      item_id: line.sku || String(line.variant_id || ''),
      item_name: line.product_title || line.title || '',
      item_category: line.product_type || '',
      item_variant: variantTitle,
      price: (line.price || 0) / 100,
      quantity: line.quantity || 1,
      discount: discountAmount,
      index: index != null ? index : line.index || 0,
      item_list_id: 'Cart',
      item_list_name: 'Cart',
    };
  }

  function cartLineElToItem(lineEl) {
    if (!lineEl) return null;
    const variantTitle = lineEl.getAttribute('data-variant-title') || '';
    return {
      item_id: lineEl.getAttribute('data-product-sku') || '',
      item_name: lineEl.getAttribute('data-product-title') || '',
      item_category: lineEl.getAttribute('data-product-type') || '',
      item_variant: variantTitle === 'Default Title' ? '' : variantTitle,
      price: parseFloat(lineEl.getAttribute('data-product-price')) || 0,
      quantity: parseInt(lineEl.getAttribute('data-quantity'), 10) || 1,
      discount: parseFloat(lineEl.getAttribute('data-discount')) || 0,
      index: parseInt(lineEl.getAttribute('data-index'), 10) || 0,
      item_list_id: lineEl.getAttribute('data-list-name') || 'Cart',
      item_list_name: lineEl.getAttribute('data-list-name') || 'Cart',
    };
  }

  function observeCartDrawerViewCart(drawer, trackedAttr, onOpen) {
    if (!drawer || typeof onOpen !== 'function') return;

    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type !== 'attributes' || mutation.attributeName !== 'class') return;

        const target = mutation.target;
        const oldClasses = (mutation.oldValue || '').trim().split(/\s+/).filter(Boolean);
        if (oldClasses.includes('active') || !target.classList.contains('active')) return;
        if (window.__vGtmSuppressDrawerViewCart) return;
        if (target.hasAttribute(trackedAttr)) return;
        if (target.hasAttribute('data-v-gtm-suppress-view-cart')) {
          target.removeAttribute('data-v-gtm-suppress-view-cart');
          return;
        }

        target.setAttribute(trackedAttr, '1');
        onOpen(target);
        setTimeout(function () {
          target.removeAttribute(trackedAttr);
        }, 500);
      });
    });

    observer.observe(drawer, {
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true,
    });
  }

  function findLegacyCartLineElement(line) {
    return (
      document.getElementById('CartItem-' + line) ||
      document.getElementById('CartDrawer-Item--' + line)
    );
  }

  function bindLegacyCartQuantityEvents() {
    if (!window.vGtm || !window.vGtm.cart || typeof window.vGtm.cart.trackLineChange !== 'function') {
      return;
    }

    const CartItemsElement = customElements.get('cart-items');
    if (!CartItemsElement || !CartItemsElement.prototype.updateQuantity) {
      return;
    }

    const originalUpdateQuantity = CartItemsElement.prototype.updateQuantity;
    if (originalUpdateQuantity.__vGtmQuantityPatched) {
      return;
    }

    CartItemsElement.prototype.updateQuantity = function (line, quantity) {
      const lineNumber = parseInt(line, 10);
      const nextQty = parseInt(quantity, 10);
      if (!Number.isNaN(lineNumber) && !Number.isNaN(nextQty)) {
        const lineEl = findLegacyCartLineElement(lineNumber);
        const previousQty = lineEl ? parseInt(lineEl.getAttribute('data-quantity'), 10) : NaN;
        if (lineEl && !Number.isNaN(previousQty) && previousQty !== nextQty) {
          const module = this.tagName === 'CART-DRAWER-ITEMS' ? 'Cart Popup' : 'Cart Page';
          window.vGtm.cart.trackLineChange(lineEl, previousQty, nextQty, module);
        }
      }

      return originalUpdateQuantity.apply(this, arguments);
    };

    CartItemsElement.prototype.updateQuantity.__vGtmQuantityPatched = true;
  }

  function bindDtcCommerceEvents(push, tracker) {
    const cartModule = 'Cart Popup';
    let lastCartAddKey = '';
    let lastCartAddTime = 0;
    let lastViewCartEmitTime = 0;

    function markRecentCartAdd(item, quantity) {
      lastCartAddKey = (item.item_id || '') + '|' + (quantity || item.quantity || 1);
      lastCartAddTime = Date.now();
    }

    function shouldSkipFetchCartAdd(item, quantity) {
      const key = (item.item_id || '') + '|' + (quantity || item.quantity || 1);
      return key === lastCartAddKey && Date.now() - lastCartAddTime < 1200;
    }

    function beginViewCartEmit() {
      const now = Date.now();
      if (now - lastViewCartEmitTime < 1200) return false;
      lastViewCartEmitTime = now;
      return true;
    }

    function emitViewCart(moduleName) {
      const mod = moduleName || cartModule;
      if (!beginViewCartEmit()) return;

      fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart.js')
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          const cartItems = (cart.items || []).map(function (item, index) {
            return shopifyLineToItem(item, index + 1);
          });
          tracker.viewCart(cartItems, { module: mod });
        })
        .catch(function (error) {
          console.error('Failed to fetch cart for view_cart:', error);
        });
    }

    window.vGtm.cart.trackAdd = function (lineItem, quantity, options) {
      const opts = typeof options === 'string' ? { module: options } : options || {};
      const item = shopifyLineToItem(lineItem);
      if (!item) return;
      item.quantity = quantity || item.quantity || 1;
      if (opts.item_list_id) item.item_list_id = opts.item_list_id;
      if (opts.item_list_name) item.item_list_name = opts.item_list_name;
      if (opts.index) item.index = opts.index;
      if (shouldSkipFetchCartAdd(item, item.quantity)) return;
      markRecentCartAdd(item, item.quantity);
      tracker.addToCart(item, {
        module: opts.module || 'Product Details Page',
        button_name: opts.button_name || 'Add to Cart',
      });

      if (opts.view_cart_after !== false && typeof window.vGtm.cart.trackView === 'function') {
        window.vGtm.cart.trackView(opts.view_cart_module || cartModule);
      }
    };

    if (!window.__vGtmCartAddFetchPatched && typeof window.fetch === 'function') {
      window.__vGtmCartAddFetchPatched = true;
      const nativeFetch = window.fetch.bind(window);
      window.fetch = function (input, init) {
        return nativeFetch(input, init).then(function (response) {
          const url = typeof input === 'string' ? input : input && input.url;
          if (!url || !/\/cart\/add(\.js)?/.test(url) || !response.ok) {
            return response;
          }

          if (window.__vGtmSuppressFetchCartAdd) {
            return response;
          }

          response
            .clone()
            .json()
            .then(function (data) {
              const item = shopifyLineToItem(data);
              if (!item || !item.item_id) return;
              const quantity = data.quantity || item.quantity || 1;
              if (shouldSkipFetchCartAdd(item, quantity)) return;
              markRecentCartAdd(item, quantity);
              tracker.addToCart(item, {
                module: 'Product Details Page',
                button_name: 'Add to Cart',
              });
            })
            .catch(function () {});

          return response;
        });
      };
    }

    window.vGtm.cart.trackView = function (moduleName) {
      emitViewCart(moduleName);
    };

    window.vGtm.cart.trackLineChange = function (lineItem, previousQty, newQty, module) {
      let item;
      if (lineItem && lineItem.nodeType === 1) {
        item = cartLineElToItem(lineItem);
      } else if (lineItem && lineItem.item_id) {
        item = lineItem;
      } else {
        item = shopifyLineToItem(lineItem);
      }
      if (!item) return;
      const prev = parseInt(previousQty, 10) || 0;
      const next = parseInt(newQty, 10) || 0;
      const mod = module || cartModule;

      if (next <= 0 && prev > 0) {
        item.quantity = prev;
        tracker.removeFromCart(item, { module: mod, button_name: 'Remove' });
        return;
      }

      if (next > prev) {
        tracker.increaseQuantity(item, { quantity: next - prev });
        return;
      }

      if (next < prev) {
        tracker.decreaseQuantity(item, { quantity: prev - next });
      }
    };

    document.querySelectorAll('script[type="application/json"][data-v-gtm-view-item]').forEach(function (node) {
      try {
        const payload = JSON.parse(node.textContent || '{}');
        if (payload && payload.items) {
          tracker.viewItem(payload.items, payload.module || 'Product Details Page');
        }
      } catch (err) {
        log('view_item parse error', err);
      }
    });

    const scrollMarks = [25, 50, 75, 90];
    const scrollFired = {};
    function onScrollTrack() {
      const doc = document.documentElement;
      const scrollTop = window.pageYOffset || doc.scrollTop;
      const height = doc.scrollHeight - doc.clientHeight;
      if (height <= 0) return;
      const percent = Math.round((scrollTop / height) * 100);
      scrollMarks.forEach(function (mark) {
        if (percent >= mark && !scrollFired[mark]) {
          scrollFired[mark] = true;
          tracker.scroll(mark);
        }
      });
    }
    window.addEventListener('scroll', onScrollTrack, { passive: true });
    onScrollTrack();

    document.addEventListener('submit', function (e) {
      const form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      const authType = form.getAttribute('data-v-gtm-auth');
      if (authType) {
        sessionStorage.setItem('vgtm_pending_auth', authType);
        return;
      }
      const action = form.getAttribute('action') || '';
      if (action.indexOf('/account/login') !== -1) {
        sessionStorage.setItem('vgtm_pending_auth', 'login');
      } else if (form.id === 'Form-create-account' || form.querySelector('[name="form_type"][value="create_customer"]')) {
        sessionStorage.setItem('vgtm_pending_auth', 'sign_up');
      }
    });

    const pendingAuth = sessionStorage.getItem('vgtm_pending_auth');
    const isLoggedIn = document.body && document.body.getAttribute('data-customer-logged-in') === 'true';
    if (pendingAuth && isLoggedIn) {
      if (pendingAuth === 'login') tracker.login('email');
      if (pendingAuth === 'sign_up') tracker.signUp();
      sessionStorage.removeItem('vgtm_pending_auth');
    }

    function observeCartDrawer(selector, moduleName) {
      observeCartDrawerViewCart(document.querySelector(selector), 'data-v-gtm-view-cart-tracked', function () {
        emitViewCart(moduleName);
      });
    }

    observeCartDrawer('dtc-cart-drawer', cartModule);

    document.addEventListener(
      'click',
      function (e) {
        const banner = e.target.closest('[data-v-gtm-banner]');
        if (banner && banner.contains(e.target) && !e.target.closest('[data-v-gtm-event], a[href], button')) {
          push('banner_click', normalizeBannerClickParams(
            {
              banner_name: banner.getAttribute('data-v-gtm-banner-name') || '',
              banner_location: banner.getAttribute('data-v-gtm-banner-location') || 'hero_banner',
              banner_type: banner.getAttribute('data-v-gtm-banner-type') || 'promotional',
              button_name: 'Banner',
              click_type: 'image',
            },
            banner
          ));
        }
      },
      true
    );

    document.querySelectorAll('[data-v-gtm-video]').forEach(function (wrap) {
      if (wrap.dataset.vGtmVideoBound) return;
      wrap.dataset.vGtmVideoBound = '1';
      const moduleName = wrap.getAttribute('data-v-gtm-module') || '';
      const onPlay = function () {
        if (wrap.hasAttribute('data-v-gtm-video-viewed')) return;
        wrap.setAttribute('data-v-gtm-video-viewed', '1');
        tracker.watchVideo({
          module: moduleName,
          button_name: wrap.getAttribute('data-v-gtm-video-name') || 'Play',
        });
      };
      wrap.querySelectorAll('video').forEach(function (video) {
        video.addEventListener('play', onPlay);
      });
      wrap.querySelectorAll('[data-dtc-video-play], [data-v-gtm-video-play]').forEach(function (btn) {
        btn.addEventListener('click', onPlay);
      });
    });
  }

  bindHeaderTopFunctionEvents(pushGA4Event);

  onReady(function () {
    bindLegacyGa4Events(trackerApi);
    bindLevoitSiteEvents(pushGA4Event);
    bindDtcEvents(pushGA4Event);
    bindDtcCommerceEvents(pushGA4Event, trackerApi);
    bindLegacyCartQuantityEvents();
    bindSkillDeclarativeEvents(pushGA4Event);
    if (config.debug || window.vGtm.debug) {
      console.log('[vGtm] Event listeners initialized');
    }
  });

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = trackerApi;
  }
})();

